<template>
  <div class="content">
    <div class="user-management">
      <div class="row">
        <div class="col-md-12">
          <div style="margin-top: 20px">
            <div class="header-actions">
              <h5 class="title">Performance Criteria</h5>
              <div v-if="isAdmin" class="actions-right">
                <button class="add-employee" @click="openCriteriaModal()">
                  <span>Add Criteria</span>
                </button>
              </div>
            </div>

            <div class="mt-4">
              <CustomTable
                :header="filteredColumns"
                :loading="loadingStates.table"
                :columns="filteredColumns"
                :data="criteriaList"
              >
                <!-- Criteria Name - First Column -->
                <template v-slot:name="{ item }">
                  <div class="criteria-name">{{ item.name || '-' }}</div>
                </template>
                <template v-slot:type="{ item }">
                  <span class="type-text">{{ item.type || '-' }}</span>
                </template>
                <template v-slot:details="{ item }">
                  <span class="text-muted details-text">{{ item.details || '-' }}</span>
                </template>
                <template v-slot:reopens="{ item }">
                  <span v-if="item.rules && item.rules.penalties && item.rules.penalties.reopens">
                    <span v-if="item.rules.penalties.reopens.every">
                      Per {{ item.rules.penalties.reopens.every }} → {{ item.rules.penalties.reopens.score }}
                    </span>
                    <span v-else-if="item.rules.penalties.reopens.limit">
                      On {{ item.rules.penalties.reopens.limit }} → {{ item.rules.penalties.reopens.score }}
                    </span>
                  </span>
                  <span v-else>-</span>
                </template>

                <template v-slot:deadlineMissed="{ item }">
                  <span v-if="item.rules && item.rules.penalties && item.rules.penalties.deadlineMissed">
                    <span v-if="item.rules.penalties.deadlineMissed.every">
                      Per {{ item.rules.penalties.deadlineMissed.every }} → {{ item.rules.penalties.deadlineMissed.score }}
                    </span>
                    <span v-else-if="item.rules.penalties.deadlineMissed.limit">
                      On {{ item.rules.penalties.deadlineMissed.limit }} → {{ item.rules.penalties.deadlineMissed.score }}
                    </span>
                  </span>

                  <span v-else>-</span>
                </template>

                <!-- Score - Fourth Column (Right Aligned) -->
                <template v-slot:score="{ item }">
                  <span :class="['score-display', item.score < 0 ? 'text-danger' : 'text-success']">
                    {{ item.score !== undefined ? item.score : '-' }}
                  </span>
                </template>

                <!-- Actions -->
                <template v-if="isAdmin" v-slot:action="{ item }">
                  <div class="action-buttons">
                    <button class="edit-btn btn-status" @click="editCriteria(item)">
                      <PencilIcon color="gray" :width="16" :height="16" />
                    </button>
                    <button class="delete-btn btn-status" @click="openDeleteModal(item)">
                      <i class="fa fa-trash" style="color: gray;"></i>
                    </button>
                  </div>
                </template>
              </CustomTable>
            </div>
          </div>

          <!-- Add/Edit Modal -->
          <ReuseableModal
            :modelValue="isCriteriaModalOpen"
            @update:modelValue="isCriteriaModalOpen = $event"
            :title="selectedCriteria ? 'Edit Criteria' : 'Add Criteria'"
            :isLarge="true"
            submitButtonText="Save"
            @submit="saveCriteria()"
            :loading="loadingStates.save"
            :performance="true"
          >
            <form @submit.prevent="saveCriteria()">
              <div class="modal-body-container">
                <div class="form-scroll-container m-0">
                  <div class="form-group row ">

                    <!-- Quick Template -->
                    <div class="col-md-12 mb-1">
                      <CustomDropdown
                        label="Quick Template"
                        :options="templateOptions"
                        :value="selectedTemplate"
                        placeholder="Choose a preset to prefill"
                        @change="handleTemplateChange"
                      />
                    </div>

                    <!-- Performance Type -->
                    <div class="col-md-12 mb-1">
                      <CustomDropdown
                        label="Type"
                        :options="performanceTypes"
                        :value="selectedType"
                        placeholder="Select which performance type this applies to"
                        :required="true"
                        @change="selectedType = $event"
                      />
                    </div>

                    <!-- Criteria Name -->
                    <div class="col-md-12 mb-3">
                      <label>Criteria Name <span style="color: red; font-size: 14px;">*</span></label>
                      <input
                        type="text"
                        class="form-control"
                        v-model.trim="name"
                        placeholder="e.g. Normal/Small Ticket"
                      />
                      <small v-if="nameError" style="display: flex; align-items: center; margin-top: 15px;">{{ nameError }}</small>
                    </div>
                    <div class="form-group row mx-auto" style="width: 100%;">
                      <!-- Duration / Count / Details -->
                      <div class="col-md-12 mb-3">
                        <label v-if="!isDurationBased">{{ isShowCountInput ? countLabel : 'Details' }}  <span style="color: red; font-size: 14px;"> {{isShowCountInput ? '*' : ''}}</span></label>
                        <div v-if="isDurationBased">
                          <div class="row">
                            <div class="col-6">
                              <label>
                                Duration From (>=) <span style="color: red; font-size: 14px;">*</span>
                              </label>
                              <input
                                type="number"
                                class="form-control"
                                v-model.number="minValue"
                                min="0"
                                placeholder="e.g. 0 or 3"
                              />
                            </div>
                            <div class="col-6 ">
                              <label>Duration To (<) <span style="color: red; font-size: 14px;">*</span></label>
                              <input
                                type="number"
                                class="form-control"
                                v-model.number="maxValue"
                                placeholder="e.g. 3 or 9"
                              />
                            </div>
                          </div>
                        </div>
                        <input
                          v-else-if="isShowCountInput"
                          type="number"
                          class="form-control mb-3"
                          v-model.number="countValue"
                          placeholder="e.g. 5"
                        />
                        <input
                          v-else
                          type="text"
                          class="form-control mb-3"
                          v-model.trim="details"
                          placeholder="Any thing about the criteria"
                        />
                      </div>

                      <!-- Reopens & Deadline Missed penalty fields -->
                      <div v-if="isDurationBased && selectedType === 'Ticket'" class="col-md-12">
                        <!-- <hr class="my-4" style="border-top: 1px solid #dee2e6;"> -->
                        <div class="row">
                          <div class="col-md-6  mb-3">
                            <label>Reopens Count <span style="color: red; font-size: 14px;">*</span></label>
                            <input type="number" class="form-control" v-model.number="reopenEvery" min="1" />
                            
                          </div>
                          <div class="col-md-6 mb-3">
                            <label>Reopens Penalty <span style="color: red; font-size: 14px;">*</span></label>
                             <input type="number" class="form-control" v-model.number="reopenPenalty" step="0.5" />
                          </div>
                          <div class="col-md-6  mb-3">
                            <label>Deadline Missed Count <span style="color: red; font-size: 14px;">*</span></label>
                            <input type="number" class="form-control" v-model.number="missedEvery" min="1" />
                          
                          </div>
                          <div class="col-md-6 mb-3">
                            <label>Deadline Missed Penalty <span style="color: red; font-size: 14px;">*</span></label>
                              <input type="number" class="form-control" v-model.number="missedPenalty" step="0.5" />
                          </div>
                        </div>
                      </div>
                      <!-- Score -->
                      <div class="col-md-6">
                        <label>Score <span style="color: red; font-size: 14px;">*</span></label>
                        <input
                          type="number"
                          class="form-control"
                          v-model.number="score"
                          step="0.5"
                          placeholder="e.g. 1 or -0.5"
                        />
                        <small v-if="scoreError" style="display: flex; align-items: center; margin-top: 15px;">{{ scoreError }}</small>
                      </div>

                      <div v-if="showEachCheckbox" class="col-md-6 mt-4">
                        <div class="form-group">
                          <div class="checkbox-box" style="display: flex; align-items: center; margin-top: 15px;">
                            <input
                              type="checkbox"
                              class="checkbox"
                              style="width: 15px; height: 15px;"
                              :id="'each-checkbox-' + (selectedCriteria ? selectedCriteria.uid : 'new')"
                              v-model="each"
                            />
                            <label
                              style="width: max-content; margin-top : 2px"
                              :for="'each-checkbox-' + (selectedCriteria ? selectedCriteria.uid : 'new')"
                            >
                              Applied on Each Count
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </ReuseableModal>
          <ReuseableModal
            :modelValue="isDeleteModalOpen"
            @update:modelValue="isDeleteModalOpen = $event"
            title="Delete Criteria?"
            :isLarge="false"
            submitButtonText="Delete"
            @submit="deleteCriteria()"
            :loading="loadingStates.delete"
          >
            <div class="modal-body">
              <p>Are you sure to permanently delete the criteria?</p>
            </div>
          </ReuseableModal>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import swal from "sweetalert";
import CustomTable from "src/components/UIComponents/CustomTable.vue";
import PencilIcon from 'src/assets/icons/PencilIcon.js';
import ReuseableModal from "src/components/UIComponents/ReuseableModal.vue";
import CustomDropdown from '../../../UIComponents/CustomDropdown.vue';
import firebase from "../../../../assets/config/firebase";
import { createActivityPayload, formatValue, getUpdatedFields, logAdminActivity } from "../../../../utils/adminLogs";
import { handleActivityNotification } from "../../../../services/notificationService";

export default {
  components: {
    CustomTable,
    PencilIcon,
    ReuseableModal,
    CustomDropdown,
  },
  props: {
    isAdmin: Boolean,
  },
  data() {
    return {
      currentUser: this.$store.state.auth.user,
      tableColumns: [
        { label: "Criteria Name", key: "name", sortable: true, width: 320 },
        { label: "Type", key: "type", width: 160 },
        { label: "Details", key: "details", width: 300 },
        { label: "Reopens", key: "reopens", width: 140 },
        { label: "Deadline Missed", key: "deadlineMissed", width: 160 },
        { label: "Score", key: "score", width: 120, align: "right" },
        { label: "Actions", key: "action", sticky: "right", width: 100 }
      ],

      criteriaList: [],
      selectedCriteria: null,
      isCriteriaModalOpen: false,
      isDeleteModalOpen: false,

      name: "",
      details: "",
      score: 0,
      selectedType: "",
      selectedTemplate: "",

      minValue: null,
      maxValue: null,
      countValue: null,
      each: false,

      reopenEvery: 5,
      reopenPenalty: -0.5,
      missedEvery: 5,
      missedPenalty: -0.5,

      performanceTypes: [
        "Ticket",
        "PR Review/Help",
        "Weekly Session",
        "Weekly Session Attendance",
        "Weekly Session Lead",
        "Random Post",
        "Interview",
        "Lead Per Project",
      ],

      criteriaPresets: [
        { name: "Partial Task from a Ticket", type: "Ticket", details: "0 hrs to  3 hrs", score: 0.5 },
        { name: "Normal/Small Ticket", type: "Ticket", details: "3 hrs to 9 hrs", score: 1 },
        { name: "Large Ticket", type: "Ticket", details: "9 hrs to 40 hrs", score: 3 },
        { name: "PR Reviews/Helps in 3 Unique Tickets", type: "PR Review/Help", details: "per 3 PRs/Helps", score: 1 },
        { name: "Weekly Session", type: "Weekly Session", details: "Rating 1-5", score: 1 },
        { name: "Weekly Session Attendance", type: "Weekly Session Attendance", details: "per session", score: 0.5 },
        { name: "Weekly Session Lead", type: "Weekly Session Lead", details: "", score: 0.5 },
        { name: "2 Random posts with Discussions", type: "Random Post", details: "per 2 posts", score: 1 },
        { name: "Intern Interview", type: "Interview", details: "Intern level", score: 0.5 },
        { name: "Jr. Interview", type: "Interview", details: "Junior level", score: 1 },
        { name: "Mid. Interview", type: "Interview", details: "Mid level", score: 1.5 },
        { name: "Lead Per Project", type: "Lead Per Project", details: "", score: 1 },
      ],

      nameError: "",
      scoreError: "",
      loadingStates: { table: false, save: false, delete: false },
    };
  },

  computed: {
    filteredColumns() {
      return this.isAdmin
        ? this.tableColumns
        : this.tableColumns.filter(c => c.key !== "action");
    },
    templateOptions() {
      return this.criteriaPresets.map(p => p.name);
    },
    isDurationBased() {
      const lower = this.name ? this.name.toLowerCase() : '';
      return this.selectedType === "Ticket" && !lower.includes("reopen") && !lower.includes("deadline");
    },
    isWeeklySession() {
      return this.selectedType === "Weekly Session";
    },
    isShowCountInput() {
      const lower = this.name ? this.name.toLowerCase() : '';
      const countTypes = ["pr review/help", "random post", "penalty", "weekly session attendance", "weekly session"];
      return countTypes.includes(this.selectedType.toLowerCase()) ||
             (this.selectedType === "Ticket" && (lower.includes("reopen") || lower.includes("deadline")));
    },
    showEachCheckbox() {
      const lower = this.name ? this.name.toLowerCase() : '';
      return this.selectedType === "Ticket" ||
             this.selectedType === "Weekly Session Attendance" ||
             this.selectedType === "Weekly Session" ||
             this.selectedType === "Penalty" ||
             lower.includes("reopen") || lower.includes("deadline") ||
             this.selectedType === "PR Review/Help" || this.selectedType === "Random Post";
    },
    countLabel() {
      return this.selectedType === "Weekly Session" ? "Rating Count" : "Count";
    }
  },

  mounted() {
    this.fetchCriteria();
  },

  methods: {
    updateFormFields() {

      if (this.minValue !== null || this.maxValue !== null || this.countValue !== null) {
        return;
      }

      if (!this.details) return;

      let cleaned = this.details.replace(/hrs/g, '').trim();

      if (cleaned.startsWith('<')) {
        this.maxValue = parseFloat(cleaned.slice(1).trim());
        this.minValue = null;
      } else if (cleaned.startsWith('>=')) {
        this.minValue = parseFloat(cleaned.slice(2).trim());
        this.maxValue = null;
      } else if (cleaned.includes('to')) {
        let [minStr, maxStr] = cleaned.split('to');
        this.minValue = parseFloat(minStr.trim());
        this.maxValue = this.minValue !== null ? parseFloat(maxStr.trim()) : null;
      } else if (cleaned.startsWith('per')) {
        const perStr = cleaned.slice(3).trim();
        const numberMatch = perStr.match(/\d+/);
        const countNum = numberMatch ? parseFloat(numberMatch[0]) : 1;
        this.countValue = countNum;
      } else {
        const lowerDetails = cleaned.toLowerCase();
        if (lowerDetails.includes('score on each')) {
          const parts = cleaned.split('on each');
          if (parts[1]) {
            const eachPart = parts[1].trim();
            const numMatch = eachPart.match(/^(\d+)/);
            if (numMatch) {
              this.countValue = parseFloat(numMatch[1]);
            } else {
              this.countValue = 1;
            }
          }
        } else if (lowerDetails.includes('score on') && lowerDetails.includes('or more')) {
          const parts = cleaned.split('on ');
          if (parts[1]) {
            const onPart = parts[1].split(' or more')[0].trim();
            this.countValue = parseFloat(onPart);
          }
        } else if (lowerDetails.includes('score on')) {
          const parts = cleaned.split('on ');
          if (parts[1]) {
            const onPart = parts[1].trim();
            const numMatch = onPart.match(/^(\d+)/) || onPart.match(/(\d+)/);
            if (numMatch) {
              this.countValue = parseFloat(numMatch[1]);
            } else {
              this.countValue = 1;
            }
          }
        } else if (lowerDetails.includes('rating')) {
          const numberMatch = cleaned.match(/\d+/);
          if (numberMatch) {
            this.countValue = parseFloat(numberMatch[0]);
          } else {
            this.countValue = 5;
          }
        }
      }
    },

    handleTemplateChange(value) {
      this.selectedTemplate = value;
      const preset = this.criteriaPresets.find(p => p.name === value);
      if (preset) {
        this.name = preset.name;
        this.selectedType = preset.type;
        this.details = preset.details;
        this.score = preset.score;

        this.reopenEvery = 5;
        this.reopenPenalty = -0.5;
        this.missedEvery = 5;
        this.missedPenalty = -0.5;

        this.updateFormFields();
      }
    },

    openCriteriaModal() {
      this.resetForm();
      this.isCriteriaModalOpen = true;
    },

    editCriteria(item) {
      this.selectedCriteria = item;
      this.name = item.name || "";
      this.details = item.details || "";
      this.score = item.score || 0;
      this.selectedType = item.type || "";
      this.selectedTemplate = "";

      if (this.isDurationBased && item.rules && item.rules.duration) {
        this.minValue = item.rules.duration.min !== undefined && item.rules.duration.min !== null
          ? item.rules.duration.min
          : null;
        this.maxValue = item.rules.duration.max !== undefined && item.rules.duration.max !== null
          ? item.rules.duration.max
          : null;
      } else {
        this.minValue = null;
        this.maxValue = null;
      }

      const penalties = (item.rules && item.rules.penalties) || {};
      const reopens = penalties.reopens || {};
      const deadlineMissed = penalties.deadlineMissed || {};

      this.reopenEvery   = reopens.every || reopens.limit || 5;
      this.reopenPenalty = reopens.score !== undefined ? reopens.score : -0.5;
      this.missedEvery   = deadlineMissed.every || deadlineMissed.limit || 5;
      this.missedPenalty = deadlineMissed.score !== undefined ? deadlineMissed.score : -0.5;
      const lowerName = this.name.toLowerCase();
      if (this.selectedType === "Ticket") {
        this.each = reopens.every !== undefined;
      } else if (this.selectedType === "PR Review/Help" || this.selectedType === "Random Post" ||
                 this.selectedType === "Weekly Session Attendance" || this.selectedType === "Weekly Session") {
        this.each = item.rules.countDivisor !== undefined;
        this.countValue = item.rules.countDivisor || item.rules.limit || null;
      } else if (lowerName.includes("reopens") || lowerName.includes("deadlines missed") || lowerName.includes("deadline")) {
        this.each = item.rules.penalty.per !== undefined;
      } else if (this.selectedType === "Penalty") {
        this.each = item.rules.penalty ? item.rules.penalty.per !== undefined : false;
      }

      this.updateFormFields();
      this.isCriteriaModalOpen = true;
    },

    resetForm() {
      this.name = "";
      this.details = "";
      this.score = 0;
      this.selectedType = "";
      this.selectedTemplate = "";
      this.selectedCriteria = null;
      this.minValue = null;
      this.maxValue = null;
      this.countValue = null;
      this.each = false;
      this.reopenEvery = 5;
      this.reopenPenalty = -0.5;
      this.missedEvery = 5;
      this.missedPenalty = -0.5;
      this.nameError = "";
      this.scoreError = "";
    },

    generateRules() {
      const lowerName = this.name.toLowerCase();

      let rules = {};

      if (this.isDurationBased) {
        rules = { duration: {}, type: "Ticket" };
        if (this.minValue !== null) rules.duration.min = this.minValue;
        if (this.maxValue !== null) rules.duration.max = this.maxValue;

        // Always save penalties (even defaults)
        rules.penalties = {
          reopens: {
            [this.each ? 'every' : 'limit']: Number(this.reopenEvery) || 5,
            score: Number(this.reopenPenalty) || -0.5
          },
          deadlineMissed: {
            [this.each ? 'every' : 'limit']: Number(this.missedEvery) || 5,
            score: Number(this.missedPenalty) || -0.5
          }
        };
      } else {
        // old logic for other types
        if (lowerName.includes("reopens")) {
          let count = this.countValue || 5;
          let key = this.each ? 'per' : 'limit';
          rules = { penalty: { [key]: count, score: this.score }, field: "reopens" };
        } else if (lowerName.includes("deadlines missed") || lowerName.includes("deadline")) {
          let count = this.countValue || 5;
          let key = this.each ? 'per' : 'limit';
          rules = { penalty: { [key]: count, score: this.score }, field: "deadlineMissed" };
        } else if (this.selectedType === "PR Review/Help") {
          let count = this.countValue || 3;
          let key = this.each ? 'countDivisor' : 'limit';
          rules = { [key]: count, type: "PR Review/Help" };
        } else if (this.selectedType === "Random Post") {
          let count = this.countValue || 2;
          let key = this.each ? 'countDivisor' : 'limit';
          rules = { [key]: count, type: "Random Post" };
        } else if (this.selectedType === "Weekly Session Attendance") {
          let count = this.countValue || 1;
          let key = this.each ? 'countDivisor' : 'limit';
          rules = { [key]: count, type: "Weekly Session Attendance" };
        } else if (this.selectedType === "Weekly Session") {
          let count = this.countValue || 5;
          let key = this.each ? 'countDivisor' : 'limit';
          rules = { [key]: count, type: "Weekly Session" };
        } else if (this.selectedType === "Lead Per Project") {
          rules = { fixed: this.score };
        } else if (this.selectedType === "Weekly Session Lead") {
          rules = { fixed: this.score };
        } else if (this.selectedType === "Interview") {
          let level = "";
          if (lowerName.includes("intern")) level = "Intern";
          if (lowerName.includes("jr")) level = "Junior";
          if (lowerName.includes("mid")) level = "Mid";
          rules = { level, score: this.score };
        } else if (this.selectedType === "Penalty") {
          let count = this.countValue || 5;
          let key = this.each ? 'per' : 'limit';
          rules = { penalty: { [key]: count, score: this.score }, field: "penalty" };
        }
      }

      return rules;
    },

    async saveCriteria() {
      let errors = [];

      if (!this.name.trim()) {
        errors.push("Criteria name is required");
      }
      if (!this.selectedType) {
        errors.push("Performance type is required");
      }
      if (isNaN(this.score)) {
        errors.push("Score must be a valid number");
      } else {
        if (this.selectedType === "Penalty") {
          if (this.score >= 0) {
            errors.push("Score for Penalty must be negative");
          }
        } else {
          if (this.score <= 0) {
            errors.push("Score must be more than 0");
          }
        }
      }

      if (this.isDurationBased) {
        if (isNaN(this.minValue) || this.minValue === null || this.minValue < 0) {
          errors.push("Duration From is required and must be greater than or equal to 0");
        }
        if (isNaN(this.maxValue) || this.maxValue === null || this.maxValue <= 0) {
          errors.push("Duration To is required and must be greater than 0");
        }
        if (!isNaN(this.minValue) && !isNaN(this.maxValue) && this.minValue >= this.maxValue) {
          errors.push("Duration From must be less than Duration To");
        }
        if (this.each) {
          if (isNaN(this.reopenEvery) || this.reopenEvery === null || this.reopenEvery < 1) {
            errors.push("Reopens Count is required and must be at least 1");
          }
          if (isNaN(this.missedEvery) || this.missedEvery === null || this.missedEvery < 1) {
            errors.push("Deadline Missed Count is required and must be at least 1");
          }
        }
        if (isNaN(this.reopenPenalty) || this.reopenPenalty === null) {
          errors.push("Reopens Penalty is required");
        }
        if (isNaN(this.missedPenalty) || this.missedPenalty === null) {
          errors.push("Deadline Missed Penalty is required");
        }
      }

      if (this.isShowCountInput) {
        if (isNaN(this.countValue) || this.countValue === null || this.countValue <= 0) {
          errors.push("Count is required and must be greater than 0");
        }
      }

      if (errors.length > 0) {
        if (errors.length === 1) {
          swal("Validation Error", errors[0], "error");
        } else {
          swal("Validation Errors", "Kindly fill all the required fields", "error");
        }
        return;
      }

      const lower = this.name.toLowerCase();
      if (this.isDurationBased) {
        let min = this.minValue;
        let max = this.maxValue;
        let detailsStr = '';
        if (min !== null && min !== '' && max !== null && max !== '') {
          detailsStr = `${min} hrs to less than ${max} hrs`;
        } else if (min !== null && min !== '') {
          detailsStr = `>= ${min} hrs`;
        } else if (max !== null && max !== '') {
          detailsStr = `< ${max} hrs`;
        }
        this.details = detailsStr;
      } else if (this.isShowCountInput) {
        let append = '';
        if (lower.includes('reopens')) append = ' reopens';
        if (lower.includes('deadlines missed') || lower.includes('deadline')) append = ' missed';
        if (this.selectedType === "PR Review/Help") append = ' PRs/Helps';
        if (this.selectedType === "Random Post") append = ' posts';
        if (this.selectedType === "Weekly Session Attendance") append = ' session';
        if (this.selectedType === "Weekly Session") append = ' rating'; // Rating-specific
        if (this.selectedType === "Penalty") append = ' occurrence';
        let effectiveCount = this.each ? ` ${this.score} score on each ${this.countValue>1 ? this.countValue : ' '}` :` ${this.score} score on ${this.countValue}`;
        this.details = effectiveCount ? `${effectiveCount}${append}` : '';
      }

      const payload = {
        name: this.name.trim(),
        type: this.selectedType,
        details: this.details.trim() || null,
        score: this.score,
        rules: this.generateRules(),
        updatedAt: new Date().toISOString()
      };

      try {
        this.loadingStates.save = true;

        if (this.selectedCriteria) {
          await firebase.updatePerformanceCriteria(this.selectedCriteria.uid, payload);
          const updatedFields = getUpdatedFields(this.selectedCriteria, payload);

          let title = `${this.currentUser.username} updated the performance criterion for ${payload.name}`;
          let description = `${this.currentUser.email} updated the performance criterion for ${payload.name}.`;

          let changes = Object.entries(updatedFields)
              .map(([key, value]) => `${key}: ${formatValue(value.old)} → ${formatValue(value.new)}`)
              .join('\n');

          await logAdminActivity(
            createActivityPayload({
              user: this.currentUser,
              title,
              description,
              changes,
              type: "performance_criteria_edit",
            })
          );

          await handleActivityNotification(
            {
              type: "performance_criteria",
              activityDescription: description,
              activityName: title,
              activityByUserName: this.currentUser.username,
              action: "Updated",
            },
            this.$store.state.auth,
            this.currentUser.uid
          );

          swal("Success!", "Criteria updated successfully", "success");
        } else {
          await firebase.addPerformanceCriteria(payload);
          
          let title = `${this.currentUser.username} added a new performance criterion named ${payload.name}`;
          let description = `${this.currentUser.email} added a new performance criterion named ${payload.name}.`;

          await logAdminActivity(
            createActivityPayload({
              user: this.currentUser,
              title,
              description,
              type: "performance_criteria_add",
            })
          );

          await handleActivityNotification(
            {
              type: "performance_criteria",
              activityDescription: description,
              activityName: title,
              activityByUserName: this.currentUser.username,
              action: "Added",
            },
            this.$store.state.auth,
            this.currentUser.uid
          );
          swal("Success!", "Criteria added successfully", "success");
        }

        await this.fetchCriteria();
        this.isCriteriaModalOpen = false;
      } catch (err) {
        console.error(err);
        swal("Error", err.message || "Failed to save criteria", "error");
      } finally {
        this.loadingStates.save = false;
      }
    },

    openDeleteModal(item) {
      this.selectedCriteria = item;
      this.isDeleteModalOpen = true;
    },

    async deleteCriteria() {
      try {
        this.loadingStates.delete = true;
        await firebase.deletePerformanceCriteria(this.selectedCriteria.uid);
        await this.fetchCriteria();

        let title = `${this.currentUser.username} deleted the performance criterion named ${this.selectedCriteria.name}`;
        let description = `${this.currentUser.email} deleted the performance criterion named ${this.selectedCriteria.name}.`;

        await logAdminActivity(
          createActivityPayload({
            user: this.currentUser,
            title,
            description,
            type: "performance_criteria_delete",
          })
        );

        await handleActivityNotification(
          {
            type: "performance_criteria",
            activityDescription: description,
            activityName: title,
            activityByUserName: this.currentUser.username,
            action: "Deleted",
          },
          this.$store.state.auth,
          this.currentUser.uid
        );

        swal("Deleted", "Criteria removed successfully", "success");
        this.isDeleteModalOpen = false;
      } catch (err) {
        swal("Error", "Failed to delete criteria", "error");
      } finally {
        this.loadingStates.delete = false;
      }
    },

    async fetchCriteria() {
      this.loadingStates.table = true;
      try {
        const list = await firebase.getAllPerformanceCriteria();
        this.criteriaList = list || [];
      } catch (err) {
        console.error(err);
        swal("Error", "Could not load criteria", "error");
      } finally {
        this.loadingStates.table = false;
      }
    }
  }
};
</script>

<style scoped>
.form-control:focus {
  box-shadow: none !important;
}
.input-group > .form-control {
  text-align: center;
}
.settings .form-group{
  margin: 0px;

}
</style>


// WEBPACK FOOTER //
// src/components/Dashboard/Views/PerformanceCriteria/PerformanceCriteria.vue